// src/components/admin/FeedbackMessageList.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaCommentDots, FaReply, FaCheckCircle, FaSpinner } from 'react-icons/fa';
import { format } from 'date-fns';

const API_BASE = process.env.REACT_APP_API_URL;

const FeedbackMessageList = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage('Authentication error: Not logged in or token expired.');
        setLoading(false);
        return;
      }
      const res = await axios.get(`${API_BASE}/api/feedback`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      const sortedFeedback = res.data.sort((a, b) => {
        const statusOrder = { 'new': 1, 'read': 2, 'replied': 3 };
        return statusOrder[a.status] - statusOrder[b.status] || new Date(b.createdAt) - new Date(a.createdAt);
      });
      setFeedbacks(sortedFeedback);
    } catch (err) {
      console.error('Error fetching feedback:', err.response?.data || err.message);
      if (err.response?.status === 401 || err.response?.status === 403) {
        setMessage('❌ Failed to load feedback. Access denied: Admins only.');
      } else if (err.response?.status === 404) {
        setMessage('❌ Failed to load feedback. API route not found. Check server.js for /api/feedback.');
      } else {
        setMessage('❌ Failed to load feedback. Please check your backend connection.');
      }
    } finally {
      setLoading(false);
    }
  }, []); // REMOVED: API_BASE from dependencies

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const openFeedbackDetails = async (feedback) => {
    setSelectedFeedback(feedback);
    setReplyMessage(feedback.reply || '');

    if (feedback.status === 'new') {
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API_BASE}/api/feedback/mark-read/${feedback._id}`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        setFeedbacks(prev => prev.map(f =>
          f._id === feedback._id ? { ...f, status: 'read' } : f
        ));
        setSelectedFeedback(prev => ({ ...prev, status: 'read' }));
      } catch (err) {
        console.error('Error marking feedback as read:', err.response?.data || err.message);
        setMessage('❌ Failed to mark feedback as read.');
      }
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      setMessage('Please type a reply message.');
      return;
    }
    if (!selectedFeedback) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/api/feedback/reply/${selectedFeedback._id}`, { replyMessage }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setMessage('✅ Reply sent successfully and feedback marked as replied!');
      setFeedbacks(prev => prev.map(f =>
        f._id === selectedFeedback._id ? { ...f, status: 'replied', reply: replyMessage } : f
      ));
      setSelectedFeedback(prev => ({ ...prev, status: 'replied', reply: replyMessage }));
    } catch (err) {
      console.error('Error sending reply:', err.response?.data || err.message);
      setMessage('❌ Failed to send reply. Please try again.');
    }
  };

  const closeDetails = () => {
    setSelectedFeedback(null);
    setReplyMessage('');
    fetchFeedback();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-red-500 text-white';
      case 'read': return 'bg-yellow-500 text-white';
      case 'replied': return 'bg-green-500 text-white';
      default: return 'bg-gray-400 text-white';
    }
  };

  return (
    <div className="bg-white p-6 rounded shadow-lg min-h-[500px]">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Feedback Management</h2>

      {message && (
        <div className={`p-3 mb-4 rounded-md ${message.startsWith('✅') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {message}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <FaSpinner className="animate-spin text-cyan-600 text-4xl" />
          <p className="ml-3 text-gray-600">Loading feedback...</p>
        </div>
      ) : feedbacks.length === 0 ? (
        <p className="text-gray-600 text-center py-8">No feedback found.</p>
      ) : (
        <div className="space-y-4">
          {feedbacks.map(fb => (
            <div
              key={fb._id}
              className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition duration-200
                ${fb.status === 'new' ? 'bg-blue-50 border-blue-200 hover:bg-blue-100' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}
                ${selectedFeedback?._id === fb._id ? 'ring-2 ring-cyan-500 border-cyan-500' : ''}
              `}
              onClick={() => openFeedbackDetails(fb)}
            >
              <div className={`px-3 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${getStatusColor(fb.status)}`}>
                {fb.status.toUpperCase()}
              </div>
              <div className="flex-grow min-w-0"> {/* Added min-w-0 to allow text wrapping */}
                {/* Removed truncate and max-w-lg; added break-words */}
                <p className="text-gray-800 text-sm mt-1 break-words">
                  <FaCommentDots className="inline-block mr-1 text-gray-500" /> {fb.message}
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  Received: {format(new Date(fb.createdAt), 'MMM dd,yyyy HH:mm')}
                </p>
              </div>
              {fb.status === 'replied' && (
                <FaCheckCircle className="text-green-500 text-2xl flex-shrink-0" title="Replied" />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Feedback Detail Modal/Panel */}
      {selectedFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
            <h3 className="text-2xl font-bold mb-4 text-gray-800">Feedback Details</h3>
            <div className="mb-4">
              <p className="font-semibold text-gray-700">Received: <span className="font-normal">{format(new Date(selectedFeedback.createdAt), 'MMM dd,yyyy HH:mm')}</span></p>
              <p className="font-semibold text-gray-700">Status: <span className={`font-normal px-2 py-0.5 rounded ${getStatusColor(selectedFeedback.status)}`}>
                {selectedFeedback.status.toUpperCase()}
              </span></p>
              <p className="font-semibold text-gray-700 mt-2">Message:</p>
              {/* Message in detail modal also uses break-words */}
              <p className="text-gray-800 p-3 bg-gray-50 border rounded break-words">{selectedFeedback.message}</p>
            </div>

            {selectedFeedback.status === 'replied' && selectedFeedback.reply && (
              <div className="mb-4">
                <p className="font-semibold text-gray-700">Your Reply:</p>
                {/* Reply in detail modal also uses break-words */}
                <p className="text-gray-800 p-3 bg-blue-50 border border-blue-200 rounded break-words">{selectedFeedback.reply}</p>
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="replyInput" className="block text-gray-700 text-sm font-bold mb-2">
                Send Reply:
              </label>
              <textarea
                id="replyInput"
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-cyan-500"
                rows="4"
                placeholder="Type your reply here..."
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                disabled={selectedFeedback.status === 'replied'}
              ></textarea>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={closeDetails}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition"
              >
                Close
              </button>
              <button
                onClick={handleSendReply}
                className={`px-4 py-2 text-white rounded-md transition
                  ${selectedFeedback.status === 'replied' ? 'bg-gray-400 cursor-not-allowed' : 'bg-cyan-600 hover:bg-cyan-700'}
                `}
                disabled={selectedFeedback.status === 'replied'}
              >
                <FaReply className="inline-block mr-2" /> Send Reply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackMessageList;
